import * as THREE from "three";
import { VISUAL } from "../config/visualConfig";
import type { Game } from "../game/Game";
import type { GameEvent } from "../game/types";
import { isSpanning } from "../game/Pieces";
import { BoardRenderer } from "./BoardRenderer";
import { Effects } from "./Effects";
import { HudRenderer } from "./HudRenderer";
import { DepthTest } from "./DepthTest";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";
import { WarpEnvironment } from "./WarpEnvironment";
import { GlowDisplay } from "./GlowDisplay";
import { ViewOrbit } from "./ViewOrbit";

export class SceneRenderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100);
  private readonly board: BoardRenderer;
  private readonly hud: HudRenderer;
  private readonly effects: Effects;
  private titleTime = 0;
  private impact = 0;
  private readonly world = new THREE.Group();
  private readonly cyberEnvironment = new WarpEnvironment();
  private readonly flashMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, toneMapped: false, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false, side: THREE.DoubleSide });
  private readonly failureFlash = new THREE.Mesh(new THREE.PlaneGeometry(100, 100), this.flashMaterial);
  private gameOverTime: number | null = null;
  private gameOverStartedAt = 0;
  private landingShake = 0;
  private elapsed = 0;
  private readonly orbit = new ViewOrbit();
  private accessibleLabel = "";
  private readonly cameraRight = new THREE.Vector3();
  private readonly cameraUp = new THREE.Vector3();
  private readonly depthTest = new DepthTest();
  private readonly display = new GlowDisplay();
  diagnosticMode = false;

  constructor(canvas: HTMLCanvasElement, private readonly game: Game) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, powerPreference: "high-performance" });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setClearColor(VISUAL.background, 1);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.15;
    this.scene.background = new THREE.Color(VISUAL.background);
    const studio = new RoomEnvironment();
    const generator = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = generator.fromScene(studio, 0.04).texture;
    this.scene.environmentIntensity = 0.4;
    studio.dispose();
    generator.dispose();

    this.board = new BoardRenderer(game);
    this.hud = new HudRenderer(game);
    this.effects = new Effects(game);
    this.world.add(this.board.group, this.effects.group, this.cyberEnvironment.group);
    this.scene.add(this.world, this.hud.group, this.depthTest.group, this.failureFlash);
    this.applyView();
    this.failureFlash.rotation.x = -VISUAL.cameraPitch;
    this.failureFlash.renderOrder = 1000;
    this.failureFlash.visible = false;
    this.addEnvironment();
    this.resize();
    window.addEventListener("resize", () => this.resize());
    this.renderer.xr.addEventListener("sessionend", () => {
      this.applyView();
      this.resize();
    });
  }

  handle(events: readonly GameEvent[]): void {
    for (const event of events) {
      if (event.type === "layer-switch") this.board.beginLayerTransition(event.from);
      else if (event.type === "layer-blocked") this.board.pulseLayer(event.target);
      else if (event.type === "line-clear") this.board.pulseLayer(event.layer);
      if (event.type === "move" || event.type === "rotate") this.board.react(0.7);
      if (event.type === "lock") {
        this.board.pulseLayer(event.piece.layer);
        if (isSpanning(event.piece.type)) this.board.pulseLayer(event.piece.layer === "front" ? "back" : "front");
        this.impact = 0.08; this.landingShake = 0.18;
      }
      if (event.type === "line-clear") {
        this.impact = 0.13;
        this.cyberEnvironment.burst(event.layer, event.rows.length);
        this.board.boostLeds(event.rows.length);
      }
      if (event.type === "game-over") { this.gameOverTime = 0; this.gameOverStartedAt = performance.now(); }
      if (event.type === "start") {
        this.board.resetLayerTransition();
        this.gameOverTime = null;
        this.landingShake = 0;
        this.impact = 0;
        this.cyberEnvironment.resetBurst();
      }
    }
    this.effects.handle(events);
  }

  update(deltaSeconds: number): void {
    this.elapsed += deltaSeconds;
    this.applyView();
    if (this.gameOverTime !== null) this.gameOverTime = Math.min(VISUAL.gameOverSeconds, (performance.now() - this.gameOverStartedAt) / 1000);
    this.landingShake = Math.max(0, this.landingShake - deltaSeconds);
    this.board.setCollapse(this.gameOverTime);
    this.impact *= Math.exp(-deltaSeconds * 18);
    this.board.group.position.y = -this.impact;
    this.board.update(deltaSeconds);
    this.hud.update();
    this.hud.animate(this.elapsed);
    this.hud.setGameOverAnimating(this.isGameOverAnimating);
    const phaseLabel = this.game.phase === "game-over" && this.isGameOverAnimating
      ? `崩壊演出中、残り${Math.ceil(VISUAL.gameOverSeconds - (this.gameOverTime ?? 0))}秒`
      : this.game.phase;
    const label = `DUAL STACK: ${phaseLabel}, score ${this.game.score}, ${this.game.mode === "alternate" ? "裏モード" : "通常モード"}, 手前: ${this.game.foregroundLayer === "front" ? "水色" : "赤"}, 視点 ${Math.round(this.viewAngles.yaw)}° / ${Math.round(this.viewAngles.pitch)}°${this.game.active && isSpanning(this.game.active.type) ? `, 紫ブロック ${this.game.active.type === "B1" ? "1×1" : "1×2"}` : ""}${this.game.isLayerSwitching ? ", 盤面スワップ中" : ""}${this.cyberEnvironment.isBursting ? ", ライン消去演出中" : ""}`;
    if (label !== this.accessibleLabel) {
      this.accessibleLabel = label;
      this.renderer.domElement.setAttribute("aria-label", label);
    }
    this.effects.update(deltaSeconds);
    this.cyberEnvironment.update(deltaSeconds, this.isGameOverAnimating ? 2 : 1);
    this.updateCameraShake();
    if (this.game.phase === "title") {
      this.titleTime += deltaSeconds;
      this.board.group.position.z = Math.sin(this.titleTime * 0.8) * 0.16;
    } else {
      this.board.group.position.z += (0 - this.board.group.position.z) * Math.min(1, deltaSeconds * 8);
    }
  }

  render(): void {
    this.display.render(this.renderer, this.scene, this.camera, !this.diagnosticMode);
  }

  get isGameOverAnimating(): boolean {
    return this.gameOverTime !== null && this.gameOverTime < VISUAL.gameOverSeconds;
  }

  setDiagnosticMode(enabled: boolean): void {
    this.diagnosticMode = enabled;
    this.depthTest.group.visible = enabled;
    this.board.group.visible = !enabled;
    this.hud.group.visible = !enabled;
    this.effects.group.visible = !enabled;
    this.cyberEnvironment.group.visible = !enabled;
    this.applyView();
  }

  setPreviewAngle(degrees: number): void {
    this.orbit.yaw = degrees * Math.PI / 180;
    this.applyView();
  }

  rotateView(x: number, y: number, seconds: number): void {
    if (this.diagnosticMode) return;
    this.orbit.rotate(x, y, seconds);
  }

  resetView(): void { this.orbit.reset(); this.applyView(); }

  get viewAngles(): { yaw: number; pitch: number } {
    return { yaw: this.orbit.yaw * 180 / Math.PI, pitch: this.orbit.pitch * 180 / Math.PI };
  }

  private applyView(): void {
    const yaw = this.diagnosticMode ? 0 : this.orbit.yaw;
    const pitch = this.diagnosticMode ? VISUAL.cameraPitch : this.orbit.pitch;
    const xr = this.renderer.xr.isPresenting;
    const cameraYaw = xr ? 0 : yaw, cameraPitch = xr ? VISUAL.cameraPitch : pitch;
    this.camera.position.set(Math.sin(cameraYaw) * Math.cos(cameraPitch) * 36, Math.sin(cameraPitch) * 36, Math.cos(cameraYaw) * Math.cos(cameraPitch) * 36);
    this.camera.lookAt(0, 0, 0);
    if (xr && !this.diagnosticMode) {
      // Changing SDK trackball settings reallocates the quilt on every event.
      // This inverse orbit gives all 66 eyes the same virtual camera movement
      // while leaving the calibrated eye poses and framebuffer intact.
      const desired = new THREE.Quaternion().setFromEuler(new THREE.Euler(-pitch, yaw, 0, "YXZ"));
      this.world.quaternion.setFromEuler(new THREE.Euler(-VISUAL.cameraPitch, 0, 0)).multiply(desired.invert());
    } else this.world.quaternion.identity();
    this.hud.setView(cameraYaw, cameraPitch);
    this.failureFlash.quaternion.setFromEuler(new THREE.Euler(-cameraPitch, cameraYaw, 0, "YXZ"));
  }

  setFocusOnly(enabled: boolean): void {
    this.depthTest.setFocusOnly(enabled);
  }

  private addEnvironment(): void {
    this.scene.add(new THREE.HemisphereLight(0x9ff8ff, 0x15051f, 0.7));
    const key = new THREE.DirectionalLight(0xffffff, 2.3);
    key.position.set(4, 7, 9);
    this.scene.add(key);
    const cyan = new THREE.PointLight(VISUAL.front, 16, 26);
    cyan.position.set(-6, 4, 5);
    const magenta = new THREE.PointLight(VISUAL.back, 13, 26);
    magenta.position.set(6, -3, -2);
    this.scene.add(cyan, magenta);
    const rim = new THREE.DirectionalLight(0x667cff, 2);
    rim.position.set(-5, 1, -4);
    this.scene.add(rim);
  }

  private updateCameraShake(): void {
    const death = this.isGameOverAnimating && !this.diagnosticMode;
    const time = this.gameOverTime ?? 0;
    const envelope = death ? Math.min(1, (VISUAL.gameOverSeconds - time) / 0.9) : 0;
    const amplitude = death ? 0.32 * envelope : 0.055 * (this.landingShake / 0.18) ** 2;
    const x = (Math.sin(this.elapsed * 131) + Math.sin(this.elapsed * 197) * 0.45) * amplitude;
    const y = (Math.sin(this.elapsed * 157) + Math.sin(this.elapsed * 223) * 0.35) * amplitude;
    const orientation = new THREE.Quaternion().setFromEuler(new THREE.Euler(
      -(this.renderer.xr.isPresenting || this.diagnosticMode ? VISUAL.cameraPitch : this.orbit.pitch),
      this.renderer.xr.isPresenting || this.diagnosticMode ? 0 : this.orbit.yaw, 0, "YXZ"));
    this.cameraRight.set(1, 0, 0).applyQuaternion(orientation);
    this.cameraUp.set(0, 1, 0).applyQuaternion(orientation);
    // Translating the complete world is equivalent to moving the camera in the
    // opposite direction. All XR eyes share it without reallocating the quilt.
    this.world.position.copy(this.cameraRight).multiplyScalar(-x).addScaledVector(this.cameraUp, -y);
    if (this.diagnosticMode) this.world.position.set(0, 0, 0);
    this.failureFlash.visible = death;
    // A smooth white pulse every 1.8 seconds, without fast color alternation.
    const pulseTime = time % 1.8;
    const pulse = pulseTime < 0.65 ? Math.sin(pulseTime / 0.65 * Math.PI) ** 2 : 0;
    this.flashMaterial.opacity = death ? 0.18 * pulse * envelope : 0;
  }

  private resize(): void {
    if (this.renderer.xr.isPresenting) return;
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }
}
